import { useFormsTemplates } from '@commenda/api';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import { FormEditorProvider } from '@commenda/ui/form-builder/hooks/useFormEditor';
import {
  ActiveFilters,
  QUERY_PARAMS,
  useFormTemplatesQueryState,
} from '@commenda/ui/form-builder/hooks/useFormTemplatesQueryState';
import { TemplatesHeader } from '@commenda/ui/form-builder/templates/TemplatesHeader';
import { TemplatesTable } from '@commenda/ui/form-builder/templates/TemplatesTable';
import { AffiliateConsolePage } from 'components/Layouts/AffiliateConsolePage';
import { useRouter } from 'next/router';
import { AffiliateFormHelper } from './affiliate-form-helper';

const AffiliateFormsPageContent = ({ firmId }: { firmId: string }) => {
  const router = useRouter();
  const {
    offset,
    limit,
    searchTerm,
    filters: activeFilters,
    setOffset,
    setSearchTerm,
    setFilters,
  } = useFormTemplatesQueryState();

  const {
    templates = [],
    isLoading,
    count,
  } = useFormsTemplates(
    firmId
      ? {
          status: activeFilters.status
            ? Array.isArray(activeFilters.status)
              ? (activeFilters.status as FormsTemplateStatus[])
              : [activeFilters.status as FormsTemplateStatus]
            : undefined,
          type: [FormsTemplateType.FORM_TEMPLATE],
          searchTerm: searchTerm || undefined,
          limit,
          offset,
        }
      : null
  );

  const handleCreateTemplate = () => {
    // A new form starts from an empty draft, so any draft left by the last one has to go.
    if (typeof window !== 'undefined') {
      localStorage.removeItem(AffiliateFormHelper.getDraftStorageKey(firmId));
    }
    router.push(AffiliateFormHelper.getFormEditorPath(firmId));
  };

  const handleFiltersChange = (filters: ActiveFilters) => {
    setFilters({ ...activeFilters, ...filters });
  };

  const tableActiveFilters = { ...activeFilters };
  delete tableActiveFilters[QUERY_PARAMS.TYPE];
  delete tableActiveFilters[QUERY_PARAMS.KEY];

  return (
    <AffiliateConsolePage>
      <div className="shrink-0">
        <TemplatesHeader
          title="Forms"
          onCreateTemplate={handleCreateTemplate}
          showDevModeToggle={false}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-2xl">
        <TemplatesTable
          activeFilters={tableActiveFilters}
          onFiltersChange={handleFiltersChange}
          templates={templates}
          isLoading={isLoading}
          onNextPage={() => setOffset(offset + limit)}
          onPreviousPage={() => setOffset(Math.max(0, offset - limit))}
          hasPreviousPage={offset > 0}
          hasNextPage={offset + limit < count}
          limit={limit}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          keyOptions={[]}
          getVersionsHref={(groupName) =>
            AffiliateFormHelper.getFormVersionsHref(firmId, groupName)
          }
          onCreateTemplate={handleCreateTemplate}
        />
      </div>
    </AffiliateConsolePage>
  );
};

/** The firm's forms plus the shared ones the backend lets it read. Scoping is enforced server
 * side, so this list sends no firm filter of its own. */
export const AffiliateFormsPage = () => {
  const router = useRouter();
  const firmId = typeof router.query.id === 'string' ? router.query.id : '';

  if (!firmId) return null;

  return (
    <FormEditorProvider
      editorPath={AffiliateFormHelper.getFormEditorPath(firmId)}
      templatesPath={AffiliateFormHelper.getFormsListHref(firmId)}
      previewPath={AffiliateFormHelper.getFormPreviewPath(firmId)}
      storageKey={AffiliateFormHelper.getDraftStorageKey(firmId)}
      defaultDevMode={false}
      showKeyManagement={false}
    >
      <AffiliateFormsPageContent firmId={firmId} />
    </FormEditorProvider>
  );
};
